/**
 * Reporting policy for best-effort *background* Firestore writes —
 * autosave persistence, hydrate, and the append-only audit flush.
 *
 * These three paths share a property: the applicant never triggers
 * them directly and never sees their failures inline. The form keeps
 * working; the write is retried (autosave re-fires on the next edit,
 * the audit batch stays buffered and re-flushes on the next nav
 * boundary, hydrate offers a Retry banner). The only question this
 * module answers is: *does this failure deserve an error-level Sentry
 * issue, or is it expected operational noise that should ride along as
 * a breadcrumb?*
 *
 * Why this exists (Sentry triage, 2026-05-30):
 *   - JAVASCRIPT-REACT-E — "Failed to get document because the client
 *     is offline" on the autosave path. A user on a flaky mobile
 *     connection (NG) momentarily lost the network. The Firestore SDK
 *     queues the write and replays it on reconnect; nothing is lost.
 *     Paging this as an error is noise.
 *   - JAVASCRIPT-REACT-D — "Missing or insufficient permissions" on
 *     the audit flush, SAME session/trace as the offline event. The
 *     audit `create` rule does a server-side `get()` on the parent
 *     application doc + a verified-token check (see firestore.rules
 *     `isOwner`). A queued audit write replayed after an offline gap
 *     can transiently fail that check (briefly-stale token, or — in QA
 *     — an application doc deleted mid-session). It self-heals on the
 *     next flush. The user-facing autosave path stays the canary for a
 *     *genuinely* broken session: it still reports permission-denied as
 *     a hard error.
 *
 * Breadcrumbs (via `reportEvent`) attach to the next real
 * `captureException`, so we keep the "what was the user doing right
 * before this crashed?" context without minting a standalone issue per
 * transient blip.
 */
import { reportError, reportEvent } from '../monitoring';

/**
 * Extract a Firestore/Firebase error code (e.g. 'unavailable',
 * 'permission-denied', 'deadline-exceeded') when present. Returns
 * `undefined` for non-Firebase errors.
 */
export function firestoreErrorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return undefined;
}

/**
 * True for transient connectivity failures the SDK retries on its own
 * once the network returns. `unavailable` is the canonical offline /
 * server-unreachable code; `deadline-exceeded` and `cancelled` are the
 * timeout / aborted-in-flight variants. Some SDK builds surface the
 * offline read rejection with only the message, so we also match that
 * text directly as a belt-and-suspenders.
 */
export function isTransientFirestoreError(err: unknown): boolean {
  const code = firestoreErrorCode(err);
  if (
    code === 'unavailable' ||
    code === 'deadline-exceeded' ||
    code === 'cancelled'
  ) {
    return true;
  }
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /client is offline/i.test(message);
}

export interface IBackgroundWriteContext {
  /** Which background subsystem failed. */
  kind: 'persistence' | 'audit';
  /** Which flow within that subsystem. */
  flow: 'autosave' | 'hydrate' | 'flush';
  /** Optional application id for error-path context. */
  applicationId?: string;
}

/**
 * Route a background-write failure to the right monitoring channel and
 * return the decision (`'error'` → Sentry issue, `'breadcrumb'` →
 * info-level breadcrumb only).
 *
 * Policy:
 *   - Transient connectivity (offline / unavailable / timeout) →
 *     breadcrumb. Always self-heals on retry.
 *   - `permission-denied` on the **audit** flush → breadcrumb. The
 *     batch retries and the autosave path is the canary for a truly
 *     broken session; a single audit-flush denial after an offline gap
 *     is not actionable.
 *   - Everything else → error-level Sentry issue, with the supplied
 *     context preserved.
 */
export function reportBackgroundWriteFailure(
  err: unknown,
  context: IBackgroundWriteContext,
): 'error' | 'breadcrumb' {
  const code = firestoreErrorCode(err);
  const transient = isTransientFirestoreError(err);
  const auditPermissionBlip =
    context.kind === 'audit' && code === 'permission-denied';

  if (transient || auditPermissionBlip) {
    reportEvent(`${context.flow}_deferred`, {
      ...context,
      reason: code ?? 'offline',
    });
    return 'breadcrumb';
  }

  reportError(err, { ...context });
  return 'error';
}
